import type { WorkbookLayerDto } from 'cadenza/workbook/api/workbook-layer-dto';
import { LayerSettingsKey } from 'cadenza/workbook/model/layer-settings-key';
import type { View } from 'cadenza/workbook/view/view';
import type { Settings } from 'cadenza/workbook/workbook-settings-util';
import { getSetting } from 'cadenza/workbook/workbook-settings-util';
import { getLayerGroupsFromView, getViewById, isWorkbookWritable } from 'cadenza/workbook/workbook.state';
import { store } from 'cadenza/store/cadenza-store';
import type { ViewDto } from 'cadenza/workbook/api/view-dto';
import type { WorkbookLayerTreeNode } from 'cadenza/workbook/api/workbook-layer-tree-node';
import type { ViewId } from 'cadenza/workbook/api/view-id';
import type { LayerId } from 'cadenza/workbook/api/layer-id';
import type { DataViewId } from 'cadenza/workbook/model/data-view-id';
import type { DataView } from 'cadenza/workbook/data-view/data-view';

export function isReferenceLayer ({ settings }: WorkbookLayerDto) {
  return Boolean(settings[LayerSettingsKey.toPersistenceKey('CATALOG_LAYER_REPOSITORY_NAME')]
      && settings[LayerSettingsKey.toPersistenceKey('CATALOG_LAYER_ITEM_NAME')]);
}

/**
 * returns data views which are not referenced or indirectly referenced by having a parent group layer
 * which is a referenced layer
 */
export function getUnreferencedDataViews (view: View) {
  if (view.type !== 'MAP') {
    return view.dataViews;
  }
  const viewDto = getViewById(store.getState(), view.viewId);
  return view.dataViews.filter(dataView => {
    const layer = viewDto.layerDefinitions[dataView.dataViewId];
    return layer && isUnreferencedLayer(layer, view.viewId);
  });
}

/**
 * returns data views which are not inside referenced groups.
 * referenced data views will be included
 */
export function getDataViewsNotWithinReferenceGroups (view: View) {
  if (view.type !== 'MAP') {
    return view.dataViews;
  }
  return view.dataViews.filter(dataView => {
    // filter out layers in reference groups
    return !isLayerInsideViewReferenceGroupLayer(view.viewId, dataView.dataViewId);
  });
}

/**
 * returns data views which are directly referenced,
 * meaning there are not referenced through a referenced parent group
 */
function getDirectReferencedDataViews (view: View) {
  if (view.type !== 'MAP') {
    return [];
  }
  const viewDto = getViewById(store.getState(), view.viewId);
  return view.dataViews.filter(dataView => {
    const layer = viewDto.layerDefinitions[dataView.dataViewId];
    return isReferenceLayer(layer);
  });
}

export function getDataViewAnalysisContextDataViews (view: View) {
  return [
    ...getUnreferencedDataViews(view),
    ...getDirectReferencedDataViews(view)
  ];
}

/**
 * returns true if this layer is not referenced or indirectly referenced by having a parent group
 * layer which is a referenced layer
 */
export function isUnreferencedLayer (layer: WorkbookLayerDto, viewId: ViewId) {
  return !isReferenceLayer(layer) && !isLayerInsideViewReferenceGroupLayer(viewId, layer.id);
}

function isLayerInsideViewReferenceGroupLayer (viewId: ViewId, layerId: LayerId) {
  const viewDto = getViewById(store.getState(), viewId);
  return (viewDto.layerTree
    .some(node => isLayerInsideReferenceGroupLayer(viewDto, layerId, node)));
}

function isLayerInsideReferenceGroupLayer (
  viewDto: ViewDto,
  layerId: LayerId,
  node: WorkbookLayerTreeNode): boolean {
  const nodeIsReferenceLayer = isReferenceLayer(viewDto.layerDefinitions[node.id]);
  if (nodeIsReferenceLayer) {
    return isLayerInsideNode(node, layerId);
  }
  return node.children.some(child => isLayerInsideReferenceGroupLayer(viewDto, layerId, child));
}

export function getReferenceGroupsWithDataViews (view: View) {
  if (view.type !== 'MAP') {
    return [];
  }
  const groups = getLayerGroupsFromView(store.getState(), view.viewId);
  return groups.filter(group => isReferenceLayer(group) && checkGroupForDataViews(view.viewId, group.id));
}

function checkGroupForDataViews (viewId: ViewId, layerId: LayerId) {
  const viewDto = getViewById(store.getState(), viewId);
  const node = viewDto.layerTree.find(layerTree => layerTree.id === layerId);
  return node && isDataViewInNode(viewDto, node);
}

function isDataViewInNode (view: ViewDto, node: WorkbookLayerTreeNode): boolean {
  const layerDefinition = view.layerDefinitions[node.id];
  if (getLayerFlags(layerDefinition).isDataViewLayer) {
    return true;
  }
  return node.children.some(layerTreeNode => isDataViewInNode(view, layerTreeNode));
}

export function isReferenceLayerById (view: View, layerId: LayerId) {
  const viewDto = getViewById(store.getState(), view.viewId);
  return isDataViewReferenceByViewDto(viewDto, layerId);
}

export function isDataViewReferenceByViewDto (viewDto: ViewDto, layerId: LayerId) {
  // layers can only be in map view
  if (viewDto.type !== 'MAP') {
    return false;
  }
  const layerFromDefinition = Object.values(viewDto.layerDefinitions).find(layer => layer.id === layerId);
  return layerFromDefinition && isReferenceLayer(layerFromDefinition);
}

function isLayerInsideNode (parentLayer: WorkbookLayerTreeNode, layerId: LayerId): boolean {
  return parentLayer.children.some(child => {
    return child.children.length > 0
      ? isLayerInsideNode(child, layerId)
      : child.id === layerId;
  });
}
export function getLayerFlags (workbookLayerDto: WorkbookLayerDto) {
  const isSingleLayer = isSingleLayerData(workbookLayerDto);
  const isGroupLayer = !isSingleLayer;
  const isLayerSketch = isSketchLayer(workbookLayerDto.settings);
  const workbookIsWritable = isWorkbookWritable(store.getState());
  const isWritableSketchLayer = isLayerSketch && workbookIsWritable;
  const hasDataViewId = Boolean(workbookLayerDto.settings['dataViewId']);
  const isDataViewLayer = isSingleLayer && hasDataViewId && !isLayerSketch;
  const isReference = isReferenceLayer(workbookLayerDto);
  const isLayerExternal = isExternalLayer(workbookLayerDto.settings);
  const hasLayerSettings = isGroupLayer || isWritableSketchLayer || isDataViewLayer || isReference || isLayerExternal;
  const isLayerClassic = isClassicLayer(workbookLayerDto.settings);
  return {
    isSingleLayer,
    isGroupLayer,
    isSketchLayer: isLayerSketch,
    workbookIsWritable,
    isWritableSketchLayer,
    isDataViewLayer,
    hasLayerSettings,
    isExternalLayer: isLayerExternal,
    isReference,
    isClassicLayer: isLayerClassic
  };
}

export function isSingleLayerData (workbookLayerDto: WorkbookLayerDto) {
  return workbookLayerDto.structureType === 'SINGLE';
}

export function isSketchLayer (settings: Settings) {
  return Boolean(getSetting(settings, LayerSettingsKey.toPersistenceKey('SKETCH_LAYER_CONTENT_IDENTIFIER')));
}

export function isExternalLayer (settings: Settings) {
  return Boolean(getSetting(settings, LayerSettingsKey.toPersistenceKey('EXTERNAL_LAYER_SERVICE_TYPE')));
}

function isClassicLayer (settings: Settings) {
  return Boolean(getSetting(settings, LayerSettingsKey.toPersistenceKey('IS_CLASSIC_LAYER')));
}

export function isOrContainsDataViewLayer (layerId: LayerId, layerTreeNode: WorkbookLayerTreeNode, layerDefinitions: Record<LayerId, WorkbookLayerDto>) {
  if (getLayerFlags(layerDefinitions[layerId]).isDataViewLayer) {
    return true;
  }
  for (const node of layerTreeNode.children) {
    if (getLayerFlags(layerDefinitions[node.id]).isDataViewLayer) {
      return true;
    }
    if (node.children.length) {
      return isOrContainsDataViewLayer(layerId, node, layerDefinitions);
    }
  }
  return false;
}

export function findNodeInTreeModel (nodes: WorkbookLayerTreeNode[], layerId: LayerId): WorkbookLayerTreeNode | undefined {
  for (const node of nodes) {
    if (node.id === layerId) {
      return node;
    }
    const foundNode = findNodeInTreeModel(node.children, layerId);
    if (foundNode) {
      return foundNode;
    }
  }
}

export function sortDataViewIdsBasedOnLayerTree (viewId: ViewId, dataViewIds: DataViewId[]): DataViewId[] {
  const view = getViewById(store.getState(), viewId);
  const layerTree = view.layerTree;
  if (!layerTree) {
    return dataViewIds;
  }
  return flatten(view.layerTree)
    .map(layerTreeNode => layerTreeNode.id)
    .filter(layerId => dataViewIds.includes(layerId as DataViewId))
    .map(layerId => layerId as DataViewId);
}

export function sortDataViewsBasedOnLayerTree (viewId: ViewId, dataViews: DataView[]): DataView[] {
  const dataViewIds = dataViews.map(dataView => dataView.dataViewId);
  const sortedDataViewIds = sortDataViewIdsBasedOnLayerTree(viewId, dataViewIds);
  const sortByIdFn = (a: DataView, b: DataView) =>
    sortedDataViewIds.indexOf(a.dataViewId) > sortedDataViewIds.indexOf(b.dataViewId) ? 1 : -1;
  return dataViews.sort(sortByIdFn);
}

function flatten (layerTreeNodes: WorkbookLayerTreeNode[]): WorkbookLayerTreeNode[] {
  return layerTreeNodes.flatMap(layerTreeNode => [ layerTreeNode, ...flatten(layerTreeNode.children) ]);
}

